import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { dateFormatterHourMinute } from "../../../format";
import { typographies } from "../../../styles/figmaTypographies";
import { Spacer } from "../Spacer";
import { TextCapitalized } from "../text/TextCapitalized";
import { TooltipDS } from "../Tooltip";
import { TripOrPlanningOptionsDisplay } from "./DayTripDetails";
import { colors } from "../../../styles/figmaColors";
import { Badge } from "../Tag/Badge";
import { Avatar } from "../Avatar/Avatar";

export interface DrivingEventBadgeProps {
  eventType: "smoothnessScore" | "supSpeedScore";
  score: number;
}

export interface TripOrPlanningDetailsProps {
  dataDisplay: TripOrPlanningOptionsDisplay;
  type: "TRIP" | "PLANNING";
  scrollPosition: number;
  hoursBarContainerWidth: number;
  hasAttachedPlanning: boolean;
  minutesSize: number;
}

const formatHourAndMinutes = (value: number): string => {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${hours}h${minutes < 10 ? "0" + minutes : minutes}`;
};

const tripDetailsOptionsDict = [
  "distance",
  "duration",
  "startDate",
  "endDate",
  "ecoDrivingScore",
];

const BASE_DRIVING_EVENT_PER_MINUTE = 0.15;

const DrivingEventBadge = (props: DrivingEventBadgeProps) => {
  const { t } = useTranslation();

  return (
    <StyledFlex>
      <Badge
        text={
          <StyledFlex>
            <TextCapitalized>
              {t(`trips.details.drivingEvents.${props.eventType}`)}
            </TextCapitalized>
          </StyledFlex>
        }
        backgroundColor={getBadgeColors(props.eventType, props.score)}
        textColor={colors["colors/text/black"]}
        typo={typographies["Body/XS"]}
        tooltip={
          <TooltipDS
            id={props.eventType}
            place={"bottom"}
            content={t(
              `trips.details.drivingEvents.${props.eventType}.tooltip`,
            )}
          />
        }
      />
    </StyledFlex>
  );
};

const getEcoDrivingColorPerPercentage = (value: number) =>
  value < 35
    ? colors["colors/system/error/error_veryLight"]
    : value > 75
      ? colors["colors/system/success/success_veryLight"]
      : colors["colors/system/warning/warning_veryLight"];

const getSmoothnessScoreColorPerPercentage = (value: number) =>
  value > 40
    ? colors["colors/system/warning/warning_veryLight"]
    : colors["colors/system/error/error_veryLight"];

const getSupSpeedScoreColorPerPercentage = (value: number) =>
  value > 30
    ? colors["colors/system/warning/warning_veryLight"]
    : colors["colors/system/error/error_veryLight"];

const StyledTripContainer = styled.div`
  display: flex;
  background-color: ${colors["colors/button/secondary/hover"]};
  border-radius: 0.5rem;
  white-space: nowrap;
`;

const getBadgeColors = (key: string, value: number | string | null): string => {
  if (value === null) return "";
  switch (key) {
    case "ecoDrivingScore":
      return getEcoDrivingColorPerPercentage(value as number);
    case "smoothnessScore":
      return getSmoothnessScoreColorPerPercentage(value as number);
    case "supSpeedScore":
      return getSupSpeedScoreColorPerPercentage(value as number);
    default:
      return colors["colors/system/informative/informative_veryLight"];
  }
};

const StyledTripDetail = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledOptionTitle = styled.div`
  color: ${colors["colors/text/black"]};
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

export const TripOrPlanningDetails = (props: TripOrPlanningDetailsProps) => {
  const { t } = useTranslation();
  const { dataDisplay, hoursBarContainerWidth } = props;
  const { driver } = props.dataDisplay;

  const drivingEventsPerMinute =
    ((dataDisplay.harshAccelerationsCount ?? 0) +
      (dataDisplay.harshBrakesCount ?? 0) +
      (dataDisplay.sharpTurnsCount ?? 0)) /
    ((new Date(dataDisplay.endDate).getTime() -
      new Date(dataDisplay.startDate).getTime()) /
      60000);

  const hasBadSmoothnessScore =
    drivingEventsPerMinute > BASE_DRIVING_EVENT_PER_MINUTE;
  const displayDrivingEvents = hasBadSmoothnessScore; // TODO: ADD OTHER DRIVING EVENTS

  const startTimeInMinutes =
    new Date(dataDisplay.startDate).getHours() * 60 +
    new Date(dataDisplay.startDate).getMinutes();

  // we need to adjust the detailsPosition to be on the right side of the screen
  let detailsPosition =
    startTimeInMinutes * props.minutesSize - props.scrollPosition / 16;

  let thresholdDivider = 0;
  let adjustmentDivider = 0;
  let isSmallScreen = false;

  if (hoursBarContainerWidth < 500) {
    thresholdDivider = 60;
    adjustmentDivider = 20;
    isSmallScreen = true;
  } else if (hoursBarContainerWidth < 1000) {
    thresholdDivider = 50;
    adjustmentDivider = 30;
    isSmallScreen = true;
  } else if (hoursBarContainerWidth < 1300) {
    thresholdDivider = 40;
    adjustmentDivider = 40;
  } else if (hoursBarContainerWidth < 2000) {
    thresholdDivider = 20;
    adjustmentDivider = 60;
  } else if (hoursBarContainerWidth > 2000) {
    thresholdDivider = 20;
    adjustmentDivider = 90;
  }

  if (detailsPosition > hoursBarContainerWidth / thresholdDivider) {
    detailsPosition =
      detailsPosition - hoursBarContainerWidth / adjustmentDivider;
  }

  const [firstName, lastName] = driver ? driver.split(" ") : ["", ""];

  return (
    <StyledAbsolute>
      <StyledFlex>
        <Spacer x={detailsPosition} />
        <StyledColumn>
          <Spacer
            y={
              // only for trip on small screen and only the trip line is displayed
              props.type === "TRIP" &&
              isSmallScreen &&
              !props.hasAttachedPlanning
                ? 2
                : props.type === "TRIP"
                  ? 6
                  : 0
            }
          />
          <StyledTripContainer>
            <Spacer x={1.5} />
            <StyledColumn>
              {driver && (
                <>
                  <Spacer y={1.5} />
                  <StyledRowCenter>
                    <TextCapitalized>
                      <Avatar
                        size="M"
                        color="mint"
                        firstName={firstName}
                        lastName={lastName}
                      />
                    </TextCapitalized>
                    <Spacer x={0.5} />
                    {firstName} {lastName}
                  </StyledRowCenter>
                </>
              )}
              <Spacer y={1.5} />
              <StyledFlex>
                {tripDetailsOptionsDict.map((option, index) => {
                  const tripDetailsValue = dataDisplay[
                    option as keyof TripOrPlanningOptionsDisplay
                  ] as string | number;

                  const hasDataDisplayValue =
                    dataDisplay[
                      option as keyof TripOrPlanningOptionsDisplay
                    ] !== null &&
                    dataDisplay[
                      option as keyof TripOrPlanningOptionsDisplay
                    ] !== undefined;
                  return (
                    <div key={index + "trip"}>
                      {hasDataDisplayValue && (
                        <StyledFlex>
                          {index !== 0 && <Spacer x={1.5} />}
                          <StyledTripDetail>
                            <StyledOptionTitle>
                              <TextCapitalized>
                                {t(`trips.details.${option}.title`)}
                              </TextCapitalized>
                            </StyledOptionTitle>
                            <Spacer y={0.5} />
                            <Badge
                              text={
                                <StyledFlex>
                                  {option === "startDate" ||
                                  option === "endDate"
                                    ? dateFormatterHourMinute(
                                        new Date(tripDetailsValue as string),
                                      ).replaceAll(":", "h")
                                    : option === "distance" ||
                                        option === "ecoDrivingScore"
                                      ? Number(tripDetailsValue)
                                          .toFixed(1)
                                          .toString()
                                      : option === "duration"
                                        ? formatHourAndMinutes(
                                            tripDetailsValue as number,
                                          )
                                        : tripDetailsValue}
                                  {t(`trips.details.${option}.units`) !==
                                    "" && (
                                    <>
                                      <Spacer x={0.25} />
                                      {t(`trips.details.${option}.units`)}
                                    </>
                                  )}
                                </StyledFlex>
                              }
                              backgroundColor={getBadgeColors(
                                option,
                                tripDetailsValue,
                              )}
                              textColor={colors["colors/text/black"]}
                              typo={typographies["Body/XS"]}
                            />
                          </StyledTripDetail>
                        </StyledFlex>
                      )}
                    </div>
                  );
                })}
                {displayDrivingEvents && (
                  <>
                    <Spacer x={1.5} />
                    <StyledFlex>
                      <StyledTripDetail>
                        <StyledOptionTitle>
                          <TextCapitalized>
                            {t(`trips.details.drivingEvents.title`)}
                          </TextCapitalized>
                        </StyledOptionTitle>
                        <Spacer y={0.5} />
                        <StyledFlex>
                          {hasBadSmoothnessScore && (
                            <DrivingEventBadge
                              eventType="smoothnessScore"
                              score={
                                (1 -
                                  Math.max(
                                    drivingEventsPerMinute -
                                      BASE_DRIVING_EVENT_PER_MINUTE,
                                    0,
                                  )) *
                                100
                              }
                            />
                          )}
                        </StyledFlex>
                      </StyledTripDetail>
                    </StyledFlex>
                  </>
                )}
                {dataDisplay.clientName && (
                  <>
                    <Spacer x={1.5} />
                    <StyledFlex>
                      <StyledTripDetail>
                        <StyledOptionTitle>
                          <TextCapitalized>
                            {t(`trips.details.clientName.title`)}
                          </TextCapitalized>
                        </StyledOptionTitle>
                        <Spacer y={0.5} />
                        <StyledFlexClientName>
                          {dataDisplay.clientName}
                        </StyledFlexClientName>
                      </StyledTripDetail>
                    </StyledFlex>
                  </>
                )}
              </StyledFlex>
              <Spacer y={1.5} />
            </StyledColumn>
            <Spacer x={1.5} />
          </StyledTripContainer>
        </StyledColumn>
      </StyledFlex>
    </StyledAbsolute>
  );
};

const StyledAbsolute = styled.div`
  position: absolute;
  pointer-events: none;
  z-index: 100;
`;

export const StyledFlex = styled.div`
  display: flex;
`;

export const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledFlexClientName = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
